/* eslint-disable no-console */
import {
  FetchResult,
  LazyQueryExecFunction,
  MutationFunctionOptions,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import { Turnstile } from '@marsidev/react-turnstile';
import useLocation from '@website/hooks/useLocation';
import EventFan, { Ecommerce, useEventFan } from 'eventfan';
import Cookies from 'js-cookie';
import Link from 'next/link';
import Router, { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { buildURL } from 'react-imgix';
import { Store } from '../../Store';
import useGeolocation from '../../hooks/geoLocation/useGeolocation';
import {
  CountryCodes,
  CreateBookingDocument,
  CreateBookingInput,
  CreateBookingMutation,
  GroupForBookingFormQuery,
  TransactionAddressInput,
  TrustMyTravelAuthorisationCodeDocument,
  TrustMyTravelAuthorisationCodeQuery,
  TrustMyTravelAuthorisationCodeQueryVariables,
} from '../../types/graphqlOperations';
import Address from '../forms/fields/Address';
import { SubmitButton } from '../forms/fields/SubmitButton';
import { getImageUrl } from '../media/utils/imgix';
import ErrorPage from '../utilities/ErrorPage';
import LoadingPage from '../utilities/LoadingPage';
import BookingFormLayout from './subComponents/BookingFormLayout';
import CheckoutPrices, {
  CheckoutPricesSubmitValues,
} from './subComponents/CheckoutPrices';
import OrderFAQ from './subComponents/OrderFAQ';
import TMTStyle from './tmt/TMTStyle';
import { useTMTScript } from './tmt/script';
import setupModal from './tmt/setupModal';
import { Transaction } from './tmt/types';
import { tmtErrorAlert } from './utils/tmtErrorAlert';

// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, @typescript-eslint/no-use-before-define, no-var
var _AvantMetrics: any[] = _AvantMetrics || [];

// eslint-disable-next-line @typescript-eslint/no-shadow
declare function AvantMetricsFunction(): void;

/**
 * Format the mutation input
 *
 * @param values Form values
 * @param group Legacy Group
 * @param userID User UUID
 * @param spreedlyToken Spreedly card token
 */
export function formatMutationInput(
  values: BookingFormOnSubmitValues,
  group: Pick<GroupForBookingFormQuery['group'], 'guestPrice' | 'id'>,
  userID: string,
  transaction?: Pick<Transaction, 'bookings' | 'id'>,
  ipAddress?: string,
): CreateBookingInput {
  const facebookBrowserID = Cookies.get('_fbp');
  const facebookClickID = Cookies.get('_fbc');

  const input: CreateBookingInput = {
    group: {
      id: group.id,
      guestPrice: {
        amountZD: group.guestPrice.amountZD,
        currency: group.guestPrice.currency,
      },
    },
    user: {
      id: userID,
    },
    spacesRequired: parseInt(values.prices.spacesRequired),
    payment: undefined,
    credit: undefined,
    tracking: {
      userAgent: navigator.userAgent,
      facebookBrowserID,
      facebookClickID,
      ipAddress,
    },
  };

  // Add payment if it is set
  if (parseInt(values.prices.payment) > 0) {
    input.payment = {
      amount: {
        amountZD: parseInt(values.prices.payment),
        currency: group.guestPrice.currency,
      },
      card: {
        address: values.address as any,
      },
      tmtBookingID: transaction!.bookings[0].id,
      tmtTransactionID: transaction!.id,
    };
  }

  // Add credit if it is set
  if (parseInt(values.prices.credit) > 0) {
    input.credit = { amountZD: parseInt(values.prices.credit) };
  }

  return input;
}

export function addressLineOneContainsLetters({
  address,
}: {
  address: Pick<BookingFormOnSubmitValues['address'], 'addressLine1'>;
}): boolean {
  if (address.addressLine1.match(/([A-Za-z])/)) {
    return true;
  }
  return false;
}

/**
 * Booking form submit handler
 *
 * @param values Form values
 * @param Apollo Client
 * @param group Legacy Group
 * @param trip Legacy Trip
 * @param user User
 * @param createBookingMutation
 * @param setError React hook form error callback
 * @param setSuccess Set success callback
 * @param setProcessing Set processing callback
 */
export async function bookingFormSubmit(
  values: BookingFormOnSubmitValues,
  tmtAuthLazyQuery: LazyQueryExecFunction<
    TrustMyTravelAuthorisationCodeQuery,
    TrustMyTravelAuthorisationCodeQueryVariables
  >,
  group: Pick<GroupForBookingFormQuery['group'], 'guestPrice' | 'id' | 'end'>,
  trip: Pick<GroupForBookingFormQuery['group']['trip'], 'address' | 'title'>,
  user: { email: string; id: string },
  createBookingMutation: (
    options?: MutationFunctionOptions<
      CreateBookingMutation,
      { input: CreateBookingInput }
    >,
  ) => Promise<
    FetchResult<CreateBookingMutation, Record<string, any>, Record<string, any>>
  >,
  setSuccess: (props: CreateBookingInput & { bookingID: string }) => void,
  setProcessing: (processing: any) => void,
  ipAddress?: string,
  track?: EventFan['track'],
  setError?: (error?: string) => void,
) {
  setProcessing(true);
  setError(undefined);

  // Scroll to the top of the page
  window.scroll({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });

  // track Checkout Step Completed
  if (track) {
    track<Ecommerce.CheckoutStepCompleted>('Checkout Step Completed', {
      step: 2,
    });
  }

  if (parseInt(values.prices.payment) > 0) {
    // Check the first line of the address includes letters as well as numbers (TMT requires this)
    if (!addressLineOneContainsLetters(values)) {
      alert(
        'The first line of your address must contain letters as well as numbers.',
      );
      setProcessing(false);
      return;
    }

    // Launch the TMT Modal
    const tmtPaymentModal = await setupModal({
      tmtAuthLazyQuery,
      currency: group.guestPrice.currency,
      endDate: group.end.utc,
      trip: {
        address: { country: trip.address.country },
        title: trip.title,
      },
      user,
      values: {
        prices: { payment: parseInt(values.prices.payment) },
        address: values.address,
        turnstileToken: values.turnstileToken,
      },
    });

    // Handle close
    tmtPaymentModal.on('modal_closed', function handleClose() {
      setProcessing(false);
    });

    // Handle navigating away
    const { router } = Router;
    router.events.on('routeChangeComplete', () => {
      document.querySelectorAll('._tmt--container').forEach((el) => {
        el.remove();
      });
    });

    // Handle token expired
    tmtPaymentModal.on('token_error', function handleTokenError(err) {
      console.log('TOKEN ERROR');
      console.log(err);

      // Close the modal (as it otherwise just shows a message of 'token expired' and doesn't have an option to retry)
      tmtPaymentModal.closeModal();

      // Open it again
      bookingFormSubmit(
        values,
        tmtAuthLazyQuery,
        group,
        trip,
        user,
        createBookingMutation,
        setSuccess,
        setProcessing,
        undefined,
        undefined,
        setError,
      );
    });

    // Handle transaction failed
    tmtPaymentModal.on(
      'transaction_failed',
      function handleTransactionError(err) {
        console.log('TRANSACTION FAILED');
        console.log(err);

        // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
        tmtPaymentModal.closeModal();

        // Open it again
        bookingFormSubmit(
          values,
          tmtAuthLazyQuery,
          group,
          trip,
          user,
          createBookingMutation,
          setSuccess,
          setProcessing,
          undefined,
          undefined,
          setError,
        );

        // Alert with the details
        tmtErrorAlert(err);
      },
    );

    // Handle transaction error (e.g. network error)
    tmtPaymentModal.on(
      'transaction_error',
      function handleTransactionError(err) {
        console.log('TRANSACTION ERROR');
        console.log(err);

        // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
        tmtPaymentModal.closeModal();

        // Open it again
        bookingFormSubmit(
          values,
          tmtAuthLazyQuery,
          group,
          trip,
          user,
          createBookingMutation,
          setSuccess,
          setProcessing,
          undefined,
          undefined,
          setError,
        );

        // Alert with the details
        setError('Lost internet connection. Please try again.');
      },
    );

    tmtPaymentModal.on(
      'transaction_timeout',
      function handleTransactionTimeout(data) {
        console.error('TRANSACTION TIMEOUT');
        console.error(data);

        // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
        tmtPaymentModal.closeModal();

        // Log booking ID. Lookup booking to establish if has a status of paid or not
        const bookingId = data?.booking_id;

        setError(
          `Transaction timed out. Please contact support with booking ID: ${bookingId}`,
        );
      },
    );

    tmtPaymentModal.on(
      'booking_logged',
      function handleTransactionTimeout(data) {
        console.log('BOOKING LOGGED');
        console.log(data);
      },
    );

    tmtPaymentModal.on(
      'booking_exists',
      function handleTransactionTimeout(data) {
        console.error('BOOKING LOGGED');
        console.error(data);

        // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
        tmtPaymentModal.closeModal();

        setError(`Something went wrong. Please contact us.`);
      },
    );

    tmtPaymentModal.on(
      'booking_error',
      function handleTransactionTimeout(data) {
        console.error('BOOKING ERROR');
        console.error(data);
      },
    );

    // transaction_result_available
    tmtPaymentModal.on(
      'transaction_result_available',
      function handleTransactionTimeout(data) {
        console.log('TRANSACTION RESULT AVAILABLE');
        console.log(data);
      },
    );

    // close_window_attempted
    tmtPaymentModal.on(
      'close_window_attempted',
      function handleTransactionTimeout(data) {
        console.error('CLOSE WINDOW ATTEMPTED');
        console.error(data);

        // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
        tmtPaymentModal.closeModal();

        setError(`Something went wrong. Please contact us.`);
      },
    );

    // Handle success
    tmtPaymentModal.on(
      'transaction_logged',
      async function handleTransactionResponse(
        transactionResponse: Transaction,
      ) {
        // Log the transaction success
        console.log('TRANSACTION SUCCESS');

        // Close the modal so we just see the loading indicator
        tmtPaymentModal.closeModal();
        setProcessing(true);

        // Format the mutation object
        const input = formatMutationInput(
          values,
          group,
          user.id,
          transactionResponse,
          ipAddress,
        );

        // Submit mutation
        try {
          const res = await createBookingMutation({ variables: { input } });
          setSuccess({
            ...input,
            bookingID: res.data.createBooking.booking.id,
          });
        } catch (error) {
          // Handle errors
          console.log('TRANSACTION API ERROR');
          console.log(error);
          // eslint-disable-next-line no-alert
          setError(error);
          setProcessing(false);
        }

        // Close the modal
        tmtPaymentModal.closeModal();
      },
    );
  } else {
    // Otherwise submit paymentless transaction
    // Set as processing
    setProcessing(true);

    try {
      const input = formatMutationInput(values, group, user.id);
      const res = await createBookingMutation({ variables: { input } });
      setSuccess({ ...input, bookingID: res.data.createBooking.booking.id });
    } catch (error) {
      // Handle errors
      // eslint-disable-next-line no-alert
      setError(error);
      setProcessing(false);
    }
  }
}

/**
 * Booking form
 *
 * Takes a booking using spreedly and then calls the `setSuccess()` callback.
 */
export default function BookingForm({
  customerCreditBalance,
  group,
}: BookingFormProps) {
  const { trip } = group;
  const { track } = useEventFan();
  const { push } = useRouter();
  const [error, setError] = useState<string>();

  // Load the TMT script
  const [scriptLoading, tmtLoadError] = useTMTScript();

  const geoLocation = useGeolocation();

  // Setup the TMT auth query (but don't run yet)
  const [tmtAuthLazyQuery] = useLazyQuery(
    TrustMyTravelAuthorisationCodeDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  // Get global state (for user)
  const { state } = useContext(Store);

  // Get user location
  const { ipAddress } = useLocation();

  // Setup react-hook-form
  const { handleSubmit, register, setValue, watch, control } =
    useForm<BookingFormOnSubmitValues>({
      defaultValues: {
        address: {
          country: geoLocation?.country?.code || CountryCodes.GB,
        },
      },
    });

  // Setup mutation function
  const [createBookingMutation] = useMutation(CreateBookingDocument);

  // Setup booking authorisation query

  // Setup success & processing state
  const [success, setSuccess] = useState<
    CreateBookingInput & { bookingID: string }
  >();
  const [processing, setProcessing] = useState<CreateBookingInput>();

  useEffect(() => {
    if (success) {
      track<Ecommerce.OrderCompleted>('Order Completed', {
        order_id: success.bookingID,
        revenue: (group.guestPrice.amountZD * success.spacesRequired) / 100,
        currency: group.guestPrice.currency,
        products: [
          {
            product_id: trip.id,
            sku: group.id,
            name: trip.title,
            brand: trip.host.name,
            variant: group.id,
            price: group.guestPrice.amountZD / 100,
            url: `${process.env.NEXT_PUBLIC_DOMAIN}/trips/${trip.urlSlug}`,
            image_url: buildURL(`${getImageUrl(trip.image.file?.key)}`),
          },
        ],
      });

      if (window && gtag && success.bookingID) {
        gtag('event', 'conversion', {
          send_to: 'AW-859214494/OuhMCLuD1fsYEJ6l2pkD',
          value: (group.guestPrice.amountZD * success.spacesRequired) / 100,
          currency: group.guestPrice.currency,
          transaction_id: success.bookingID,
        });
      }

      try {
        // eslint-disable-next-line no-underscore-dangle
        if ((window as any)._AvantMetrics) {
          // eslint-disable-next-line no-underscore-dangle
          console.log(_AvantMetrics);
        }

        // avantlink tracking
        // eslint-disable-next-line no-underscore-dangle
        (window as any)._AvantMetrics?.push([
          'order',
          {
            order_id: success.bookingID,
            amount: (group.guestPrice.amountZD * success.spacesRequired) / 100,
            currency: group.guestPrice.currency,
          },
        ]);

        // eslint-disable-next-line no-underscore-dangle
        (window as any)._AvantMetrics?.push([
          'item',
          {
            order_id: success.bookingID,
            parent_sku: trip.id,
            variant_sku: group.id,
            price: group.guestPrice.amountZD / 100,
            qty: success.spacesRequired,
            name: trip.title,
          },
        ]);

        // eslint-disable-next-line no-underscore-dangle
        if (
          AvantMetricsFunction &&
          typeof AvantMetricsFunction === 'function'
        ) {
          console.log('AvantMetricsFunction');
          AvantMetricsFunction();
        } else {
          console.log('AvantMetricsFunction not found');
        }
      } catch (e) {
        console.error('AvantMetrics Error');
        console.error(e);
      }

      push({
        pathname: '/book/[groupID]/success/[bookingID]',
        query: {
          groupID: success.group.id,
          bookingID: success.bookingID,
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  // Wait for TMT to load
  if (scriptLoading) {
    return <LoadingPage />;
  }

  if (tmtLoadError) {
    return <ErrorPage error="Failed to load" />;
  }

  if (processing) {
    return (
      <>
        <TMTStyle
          group={group}
          spaces={parseInt(watch('prices.spacesRequired'))}
        />

        {error && (
          <Alert variant="danger">
            <Alert.Heading>Payment Failed</Alert.Heading>
            <p>{error}</p>
            <p>If the error persists, please contact us. </p>
            <Link href="/help/contact" passHref legacyBehavior>
              <Alert.Link>Contact Us</Alert.Link>
            </Link>
          </Alert>
        )}

        <BookingFormLayout group={group} showHeader={false}>
          <div style={{ minHeight: '100vh' }}>
            <LoadingPage />
          </div>
        </BookingFormLayout>
      </>
    );
  }

  return (
    <>
      <BookingFormLayout group={group}>
        <div className="text-block">
          <Form
            onSubmit={handleSubmit((values) =>
              bookingFormSubmit(
                values,
                tmtAuthLazyQuery,
                group,
                trip,
                state.user,
                createBookingMutation,
                setSuccess,
                setProcessing,
                ipAddress,
                track,
                setError,
              ),
            )}
          >
            <CheckoutPrices
              customerCreditBalance={customerCreditBalance}
              group={group}
              register={register}
              setValue={setValue}
              watch={watch}
            />
            {/* Only show the address if payment is required */}
            {watch('prices.payment', '1') !== '0' && (
              <div className="mt-5">
                <h5>Billing Address</h5>
                <p className="text-sm text-muted">
                  Please enter the billing address associated with your card.
                  You'll be able to enter your card details securely, on the
                  next page.
                </p>
                <Address
                  watch={watch}
                  register={register}
                  name="address"
                  localityRequired
                  postalCodeRequired
                />
              </div>
            )}

            <Controller
              control={control}
              name="turnstileToken"
              rules={{
                required: 'Please complete the captcha',
              }}
              render={({ field }) => (
                <Row>
                  <Col xs={'auto'} className="mx-auto py-3">
                    <Turnstile
                      siteKey={
                        process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_ID ||
                        ''
                      }
                      onSuccess={(token) => {
                        field.onChange(token);
                      }}
                    />
                  </Col>
                </Row>
              )}
            />

            <Form.Group controlId="termsAndConditionsCheckbox">
              <Form.Check
                required
                label={
                  <span>
                    I accept the{' '}
                    <Link
                      href="/booking-terms"
                      className="text-muted"
                      target="_blank"
                    >
                      Terms and Conditions
                    </Link>
                  </span>
                }
                className="mt-4"
              />
            </Form.Group>

            <SubmitButton text="Next >" isSubmitting={false} className="mt-5" />
          </Form>
        </div>
        <OrderFAQ />
      </BookingFormLayout>
    </>
  );
}

/** Booking form props */
export interface BookingFormProps {
  /** The customer credit balance (in the correct currency) */
  customerCreditBalance: number;
  group: GroupForBookingFormQuery['group'];
}

/**
 * On submit values type
 *
 * Combination of all fields
 */
export type BookingFormOnSubmitValues = {
  prices: CheckoutPricesSubmitValues;
  turnstileToken?: string;
} & { address: TransactionAddressInput };
